import type { FirebaseApp } from 'firebase/app'
import type { Analytics } from 'firebase/analytics'

import { applyConsentDefaults } from './consent'

/**
 * Firebase Analytics facade — the web mirror of Android's
 * `com.e404.core.analytics.AnalyticsService`. Same event names, same
 * params (plus a `client: "web"` on every payload) so cross-client
 * funnels are a single param filter in the Firebase console.
 *
 * ### Two responsibilities
 *   1. **Events** — funnel + terminal events on the flows named in
 *      `contracts/ANALYTICS.md`. Product cares about state transitions
 *      (intent created, sheet opened, activated, failed, abandoned).
 *   2. **Crash attribution** — every `setCurrentFlow` / `setPaymentContext`
 *      / `setCurrentSquadron` call also stashes the value on a
 *      module-local `crashContext` object. `crash.ts` reads it when
 *      firing an `app_exception` event so the crash payload carries the
 *      flow + stage + Stripe PI id + squadron id alongside the stack.
 *      Firebase Analytics for web has no first-class `setCustomKey` like
 *      Crashlytics does, so we ship the context INSIDE the event params.
 *
 * ### Session id
 *   Payment + onboarding flows are bracketed by a client-generated
 *   session UUID stamped into every event. Lets us correlate the
 *   `_intent_created` at T=0s with the `_abandoned` at T=45s and know
 *   they're the same purchase attempt.
 *
 * ### Safety
 *   Every method wraps its Firebase call in try/catch. Instrumentation
 *   must NEVER be the reason a user's Stripe payment doesn't complete
 *   or their onboarding step doesn't advance. When Firebase config is
 *   missing (local dev without env vars), every method is a silent no-op.
 */

// ── Firebase init (lazy, guarded on missing config) ───────────────────────

let app: FirebaseApp | null = null
let analytics: Analytics | null = null
let initPromise: Promise<Analytics | null> | null = null

/**
 * The Firebase values, read straight off `import.meta.env`.
 *
 * Deliberately NOT via `env()`. That validates the ENTIRE schema and throws
 * if any unrelated variable is missing — so a deleted Stripe secret would
 * take analytics down with it, silently, because the caller below swallows
 * the throw. Analytics should depend on the six values it actually uses and
 * nothing else. Mirrors `signupsOpen()` in `lib/env.ts`, which reads one
 * variable directly for the same reason.
 */
function firebaseConfig() {
  const e = import.meta.env
  return {
    apiKey: e.VITE_FIREBASE_API_KEY,
    authDomain: e.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: e.VITE_FIREBASE_PROJECT_ID,
    messagingSenderId: e.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: e.VITE_FIREBASE_APP_ID,
    measurementId: e.VITE_FIREBASE_MEASUREMENT_ID,
  }
}

function hasConfig(): boolean {
  const c = firebaseConfig()
  return !!(c.apiKey && c.authDomain && c.projectId && c.appId && c.measurementId)
}

async function ensureApp(): Promise<FirebaseApp | null> {
  if (app) return app
  if (!hasConfig()) return null
  try {
    const { getApps, initializeApp } = await import('firebase/app')
    // Reuse the same app instance the messaging module minted if it's
    // already there — Firebase throws on double-initializeApp.
    const existing = getApps()[0]
    app = existing ?? initializeApp(firebaseConfig())
    return app
  } catch {
    return null
  }
}

async function ensureAnalytics(): Promise<Analytics | null> {
  if (analytics) return analytics
  if (initPromise) return initPromise
  initPromise = (async () => {
    const a = await ensureApp()
    if (!a) return null
    try {
      const { getAnalytics, isSupported } = await import('firebase/analytics')
      // `isSupported` returns false in headless/lighthouse harnesses, on
      // Safari's private-browsing mode, and when the browser blocks
      // cookies entirely.
      const ok = await isSupported()
      if (!ok) return null
      // Consent Mode defaults must be in the dataLayer BEFORE getAnalytics()
      // — that call is what loads gtag, and defaults set afterwards are not
      // honoured for the page view that just happened. See ./consent.ts.
      await applyConsentDefaults()
      analytics = getAnalytics(a)
      return analytics
    } catch {
      return null
    }
  })()
  return initPromise
}

/**
 * Start Analytics at app boot.
 *
 * Everything else in this module initialises lazily, on the first event.
 * That was the bug: every caller of an event function sits behind
 * authentication, and with signups closed nobody reached one — so
 * `getAnalytics()` never ran and production sent Google nothing at all,
 * despite the measurement id being correctly configured.
 *
 * Safe to call more than once; `ensureAnalytics` memoises.
 */
export function initAnalytics(): void {
  void ensureAnalytics()
}

// ── Crash-context state (read by crash.ts) ────────────────────────────────

interface CrashContext {
  flow: string | null
  stage: string | null
  sessionId: string | null
  squadronId: string | null
  stripePiId: string | null
  tierId: string | null
  amountPence: number | null
  userId: string | null
}

const crashContext: CrashContext = {
  flow: null,
  stage: null,
  sessionId: null,
  squadronId: null,
  stripePiId: null,
  tierId: null,
  amountPence: null,
  userId: null,
}

/** Read-only snapshot for `crash.ts` — never mutate the returned object. */
export function getCrashContext(): Readonly<CrashContext> {
  return crashContext
}

// ── Event recording primitive ─────────────────────────────────────────────

interface EventParams {
  [key: string]: string | number | boolean | undefined | null
}

/**
 * Fire an event to Firebase Analytics. Always stamps `client: "web"` +
 * the current session id if a flow is active. Silent no-op when Firebase
 * isn't configured or unsupported — instrumentation must never disrupt
 * the flow it's measuring.
 */
async function record(name: string, params: EventParams = {}): Promise<void> {
  try {
    const a = await ensureAnalytics()
    if (!a) return
    const payload: Record<string, string | number | boolean> = { client: 'web' }
    if (crashContext.sessionId && !('session_id' in params)) {
      payload.session_id = crashContext.sessionId
    }
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null) payload[k] = v
    }
    const { logEvent } = await import('firebase/analytics')
    logEvent(a, name, payload)
  } catch {
    // Analytics failures never surface.
  }
}

// ── Identity ──────────────────────────────────────────────────────────────

/**
 * Tag the current Firebase user identity. Called from the auth hook
 * whenever the profile query resolves or invalidates. Pass null on
 * sign-out to detach the identity.
 */
export async function setIdentity(
  user: { id: string; userType?: string; role?: string; subscriptionTier?: string } | null,
): Promise<void> {
  crashContext.userId = user?.id ?? null
  try {
    const a = await ensureAnalytics()
    if (!a) return
    const { setUserId, setUserProperties } = await import('firebase/analytics')
    setUserId(a, user?.id ?? null)
    if (user) {
      setUserProperties(a, {
        user_type: user.userType ?? '',
        user_role: user.role ?? '',
        squadron_tier: user.subscriptionTier ?? '',
      })
    }
  } catch {
    // ignore
  }
}

// ── Flow context ──────────────────────────────────────────────────────────

/**
 * Mark the current flow the caller is in. Rotates the `sessionId` UUID
 * when the flow name changes so distinct attempts don't cross-pollute.
 * Nested flows aren't modeled — the last `setCurrentFlow` wins.
 */
export function setCurrentFlow(flow: string, stage: string): void {
  if (crashContext.flow !== flow) {
    crashContext.sessionId = crypto.randomUUID()
    // New flow — reset stale payment context from a previous attempt.
    crashContext.stripePiId = null
    crashContext.tierId = null
    crashContext.amountPence = null
  }
  crashContext.flow = flow
  crashContext.stage = stage
}

/**
 * Attach the Stripe PaymentIntent id + tier id to the current flow's
 * crash context. Call after `setCurrentFlow` when those ids are known.
 * A null passed here PRESERVES the previous value on that slot rather
 * than clearing it — successive stages of the same flow can partially
 * update the context without knowing the whole set of values.
 */
export function setPaymentContext(
  piId?: string | null,
  tierId?: string | null,
  amountPence?: number | null,
): void {
  if (piId != null) crashContext.stripePiId = piId
  if (tierId != null) crashContext.tierId = tierId
  if (amountPence != null) crashContext.amountPence = amountPence
}

/** Pass null on squadron departure to detach. */
export function setCurrentSquadron(squadronId: string | null): void {
  crashContext.squadronId = squadronId
}

function clearFlow(): void {
  crashContext.flow = null
  crashContext.stage = null
  crashContext.sessionId = null
  crashContext.stripePiId = null
  crashContext.tierId = null
  crashContext.amountPence = null
}

/**
 * Peek at the current flow + stage so lifecycle callers (visibilitychange
 * / beforeunload in crash.ts) can decide whether to fire an abandonment
 * event without per-flow state threaded through every call site.
 */
export function currentFlowSnapshot(): { flow: string; stage: string } | null {
  if (!crashContext.flow || !crashContext.stage) return null
  return { flow: crashContext.flow, stage: crashContext.stage }
}

// ── Payment funnel primitives ────────────────────────────────────────────

export type PaymentFlow =
  | 'squadron_create'
  | 'member_join'
  | 'tier_activate'
  | 'tier_switch'
  | 'fee_change_accept'
  | 'tournament_join'
  | 'prize_claim'

export function paymentIntentCreated(
  flow: PaymentFlow,
  squadronId: string | null,
  tierId: string | null,
  amountPence: number | null,
): void {
  setCurrentFlow(flow, 'intent_created')
  setPaymentContext(null, tierId, amountPence)
  if (squadronId) setCurrentSquadron(squadronId)
  void record(`${flow}_intent_created`, {
    flow,
    squadron_id: squadronId,
    tier_id: tierId,
    amount_pence: amountPence,
  })
}

export function paymentSheetOpened(flow: PaymentFlow, piId: string | null): void {
  setCurrentFlow(flow, 'sheet_opened')
  setPaymentContext(piId, null, null)
  void record(`${flow}_payment_sheet_opened`, {
    flow,
    stripe_pi_id: piId,
  })
}

/** outcome: "completed" | "canceled" | "failed" */
export function paymentSheetResult(
  flow: PaymentFlow,
  outcome: 'completed' | 'canceled' | 'failed',
  errorCode?: string | null,
): void {
  setCurrentFlow(flow, `sheet_result:${outcome}`)
  void record(`${flow}_payment_sheet_result`, {
    flow,
    outcome,
    error_code: errorCode ?? null,
  })
}

export function paymentActivationAttempted(flow: PaymentFlow): void {
  setCurrentFlow(flow, 'activation_attempted')
  void record(`${flow}_activation_attempted`, { flow })
}

export function paymentActivationSuccess(flow: PaymentFlow): void {
  setCurrentFlow(flow, 'activation_success')
  void record(`${flow}_activation_success`, { flow })
  // Terminal — clear the flow so any post-payment work isn't mis-attributed.
  clearFlow()
}

export function paymentActivationFailed(flow: PaymentFlow, reason: string): void {
  setCurrentFlow(flow, 'activation_failed')
  void record(`${flow}_activation_failed`, { flow, reason })
  clearFlow()
}

export function paymentRetryAttempted(flow: PaymentFlow, fromStage: string): void {
  setCurrentFlow(flow, `retry:${fromStage}`)
  void record(`${flow}_retry_attempted`, {
    flow,
    from_stage: fromStage,
  })
}

/**
 * Fired from `visibilitychange` (backgrounded) / `beforeunload` when a
 * payment or onboarding flow is `_started` without a terminal event.
 * Best-effort — an abrupt tab close may miss the send. We accept that
 * in exchange for the funnel drop-off visibility.
 */
export function flowAbandoned(flow: string, lastStage: string): void {
  void record(`${flow}_abandoned`, {
    flow,
    last_stage: lastStage,
  })
  clearFlow()
}

// ── Acquisition funnel (pre-login) ───────────────────────────────────────
//
// Everything before an account exists. See `contracts/ANALYTICS.md` §
// "Acquisition funnel". `page_view` lives in components/PageViewTracker
// because it is driven by the router, not by a user action.

/**
 * A marketing call-to-action was clicked.
 *
 * One event with params rather than a name per button, so adding a CTA needs
 * no taxonomy change and GA4 can slice by section or by button.
 */
export function ctaClicked(
  cta: string,
  location: string,
  destination: string,
): void {
  void record('cta_clicked', { cta, location, destination })
}

/**
 * Someone joined the beta waitlist.
 *
 * While signups are closed this is the ONLY conversion the site can produce,
 * so it is the terminal event of the acquisition funnel.
 *
 * Counts successful submissions, not unique people: the endpoint returns the
 * same 200 for a new address, a duplicate and a bot, deliberately, so the
 * client cannot tell them apart. Deduplicate on the stored `WAITLIST#` rows.
 */
export function waitlistJoined(source: string): void {
  void record('waitlist_joined', { source })
}

// ── Identity (contracts/ANALYTICS.md § Identity) ─────────────────────────

/** Every sign-up or sign-in step render. */
export function signUpStepViewed(
  step: string,
  path?: string,
  userType?: string,
): void {
  void record('sign_up_step_viewed', { step, path, user_type: userType })
}

/**
 * Terminal on sign-up.
 *
 * Fired after `confirmSignUp` succeeds — NOT after `signUp`. `signUp` leaves
 * the account unconfirmed, so firing there would count everyone who never
 * verified their email and silently inflate the conversion rate. The Google
 * path arrives already confirmed and fires on its callback.
 */
export function signUpComplete(method: string, userType?: string): void {
  void record('sign_up_complete', { method, user_type: userType })
  clearFlow()
}

/** Terminal on sign-in. */
export function signInComplete(method: string): void {
  void record('sign_in_complete', { method })
}

// ── Onboarding events (parity with Android) ──────────────────────────────

export function onboardingStarted(path: string, userType: string): void {
  setCurrentFlow('onboarding', 'started')
  void record('onboarding_started', { path, user_type: userType })
}

export function onboardingStepViewed(step: string, path: string): void {
  setCurrentFlow('onboarding', step)
  void record('onboarding_step_viewed', { step, path })
}

export function onboardingComplete(path: string): void {
  void record('onboarding_complete', { path })
  clearFlow()
}

/**
 * The path-selection fork event — one of the highest drop-off points in
 * the funnel per the plan doc. `path` is one of STANDARD / MILITARY /
 * CIVILIAN. Fired once, on selection tap.
 */
export function onboardingPathSelected(path: string): void {
  setCurrentFlow('onboarding', 'path_selected')
  void record('onboarding_path_selected', { path })
}

/** Phase 2: age-verification sub-flow (Yoti / D.1 self-attest). */
export function ageVerificationStarted(): void {
  void record('age_verification_started', {})
}

export function ageVerificationSubmitted(): void {
  void record('age_verification_submitted', {})
}

export function ageVerificationApproved(): void {
  void record('age_verification_approved', {})
}

export function ageVerificationRejected(reason?: string | null): void {
  void record('age_verification_rejected', { reason: reason ?? null })
}

/** Fired on the games step when the user commits their selection. */
export function gamesAdded(count: number): void {
  void record('games_added', { count })
}

export function callsignSet(callsign: string): void {
  void record('callsign_set', { callsign_length: callsign.length })
}

export function squadronSearched(query: string, resultCount: number): void {
  void record('squadron_searched', {
    query_length: query.length,
    result_count: resultCount,
  })
}

export function squadronSelected(squadronId: string, requiresMilitary: boolean): void {
  void record('squadron_selected', {
    squadron_id: squadronId,
    requires_military: requiresMilitary,
  })
}

export function squadronCreated(tier: string, playstyle: string, platform: string): void {
  void record('squadron_created', { tier, playstyle, platform })
}

export function squadronJoined(squadronId: string): void {
  setCurrentSquadron(squadronId)
  void record('squadron_joined', { squadron_id: squadronId })
}

/**
 * Fired the moment the user leaves a squadron, whether by voluntary
 * leave, admin kick, ACCEPTED-then-CANCEL_AT_PERIOD_END, or the
 * lapsed-tenant sweep. `role` distinguishes OWNER/ADMIN/MEMBER so
 * dashboards can segment high-value churn from routine turnover.
 * `reason` is a free-form short slug ("voluntary", "kicked",
 * "fee_change_decline", "cancel_period_end", "lapsed_sweep", "leave_or_activate").
 */
export function squadronLeft(
  squadronId: string,
  role: string,
  reason: string,
): void {
  void record('squadron_left', {
    squadron_id: squadronId,
    role,
    reason,
  })
}

/**
 * Account deletion funnel — the user opens the delete screen.
 * Terminal outcomes are `account_deletion_confirmed` (the user
 * proceeded past the type-DELETE confirmation and hit submit) and
 * `account_deletion_canceled` (they backed out).
 */
export function accountDeletionStarted(): void {
  void record('account_deletion_started', {})
}

export function accountDeletionConfirmed(reason?: string | null): void {
  void record('account_deletion_confirmed', {
    reason: reason ?? null,
  })
}

export function accountDeletionCompleted(): void {
  void record('account_deletion_completed', {})
}

export function accountDeletionCanceled(): void {
  void record('account_deletion_canceled', {})
}

// ── Integrations (Discord scheduled-events sync) — Phase 5 ─────────────

/**
 * Owner dismissed the Dashboard "Connect Discord" nudge card.
 * `trigger` is one of `not_now` (ghost button) or `close_icon` (X).
 * Both persist a per-squadron dismiss flag client-side; the trigger
 * param segments them for later A/B on which affordance is more
 * legible. Mirrors Android's `discordConnectNudgeDismissed`.
 */
export function discordConnectNudgeDismissed(trigger: 'not_now' | 'close_icon'): void {
  void record('discord_connect_nudge_dismissed', { trigger })
}

/**
 * Owner tapped the primary "Connect Discord" CTA on the Dashboard
 * nudge. Distinct from tapping the Settings-tab Discord card so the
 * funnel can attribute entry paths correctly.
 */
export function discordConnectNudgeCtaClicked(): void {
  void record('discord_connect_nudge_cta_clicked', {})
}

/**
 * Fired once per connect flow when the client observes
 * `discordGuildId` transitioning from null to non-null. Guarded by
 * the connect-mutation's onSuccess so re-renders don't re-fire it.
 * Backend has no matching `DISCORD_LINKED` audit row today, so this
 * client event is the only connect-side signal in the funnel.
 */
export function discordBotConnected(): void {
  void record('discord_bot_connected', {})
}

/**
 * Sync-events modal opened. `trigger` is one of
 * `auto_after_connect` (fired from the onSuccess of the OAuth flow)
 * or `manual` (Sync Events button on the connected bot card).
 */
export function discordSyncEventsOpened(
  trigger: 'auto_after_connect' | 'manual',
): void {
  void record('discord_sync_events_opened', { trigger })
}

/**
 * GET /discord/scheduled-events succeeded. `tier` is the response's
 * tier string (`FREE` | `LEVEL_1` | `LEVEL_2` | `LEVEL_3`);
 * `event_count` is `response.events.length`.
 */
export function discordSyncEventsLoaded(eventCount: number, tier: string): void {
  void record('discord_sync_events_loaded', {
    event_count: eventCount,
    tier,
  })
}

/**
 * GET failed. `errorCode` carries the backend errorCode when
 * present (notably `DISCORD_NOT_CONNECTED` for 409). Pass null / omit
 * when the failure has no structured code (network / other).
 */
export function discordSyncEventsLoadFailed(errorCode?: string | null): void {
  void record('discord_sync_events_load_failed', {
    error_code: errorCode ?? null,
  })
}

/**
 * Batch import mutation completed. Counts should sum to `batchSize`
 * for a well-formed response — a shortfall means the server dropped
 * rows on the wire (rare — currently no client-side backfill).
 */
export function discordSyncEventsImportResult(
  importedCount: number,
  skippedCount: number,
  failedCount: number,
  batchSize: number,
): void {
  void record('discord_sync_events_import_result', {
    imported_count: importedCount,
    skipped_count: skippedCount,
    failed_count: failedCount,
    batch_size: batchSize,
  })
}

/**
 * Low-level record for events not yet covered by a typed primitive.
 * Prefer the typed helpers above; use this for ad-hoc events during
 * initial migration.
 */
export function recordEvent(name: string, params: EventParams = {}): void {
  void record(name, params)
}
